import { ConfigProvider, Form, Radio, TableProps } from 'antd'
import vi_VN from 'antd/es/locale/vi_VN'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { apiGetOutletRegionReport } from '../../api'
import FormSearch from '../../components/form'
import CompanySelect from '../../components/outlet/CompanySelect'
import DateRangePresets from '../../components/outlet/DateRangePresets'
import OutletSelect from '../../components/outlet/OutletSelect'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { IRegionRow } from '../../models/outlet.modal'
import { toastError } from '../../utils/toast'

const formatNumber = (v?: number | null) => (v == null ? '—' : v.toLocaleString('vi-VN'))
const formatCurrency = (v?: number | null) => (v == null ? '—' : `${v.toLocaleString('vi-VN')}₫`)
const formatPercent = (rate?: number | null) => (rate == null ? '—' : `${(rate * 100).toFixed(1)}%`)

type SearchState = {
    fromDate?: string
    toDate?: string
    cpCodes?: string[]
    outletIds?: string[]
    groupBy: 'province' | 'district'
}

export default function OutletRegionReport() {
    const [data, setData] = useState<IRegionRow[]>([])
    const [search, setSearch] = useState<SearchState>({ groupBy: 'province' })
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp: any = await apiGetOutletRegionReport({
                fromDate: search.fromDate,
                toDate: search.toDate,
                cpCodes: search.cpCodes,
                outletIds: search.outletIds,
                groupBy: search.groupBy,
            })
            setData(resp?.data?.data ?? [])
        } catch (error) {
            console.log(error)
            toastError('Không tải được báo cáo khu vực')
            setData([])
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, search])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleSearch = (values: any) => {
        const range = values.range as [dayjs.Dayjs, dayjs.Dayjs] | undefined
        setSearch({
            fromDate: range?.[0]?.format('YYYY-MM-DD'),
            toDate: range?.[1]?.format('YYYY-MM-DD'),
            cpCodes: values.cpCodes,
            outletIds: values.outletIds,
            groupBy: values.groupBy || 'province',
        })
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({ groupBy: 'province' })
    }

    const columns: TableProps<IRegionRow>['columns'] = [
        {
            key: 'province',
            title: 'Khu vực',
            dataIndex: 'province',
            render: v => v || '—',
        },
        ...(search.groupBy === 'district'
            ? [{
                key: 'district',
                title: 'Q/Huyện',
                dataIndex: 'district' as const,
                render: (v: string | undefined) => v || '—',
            }]
            : []),
        {
            key: 'outletCount',
            title: 'Số đại lý',
            align: 'right',
            render: (_, r) => formatNumber(r.outletCount),
        },
        {
            key: 'simAllocated',
            title: 'SIM giao',
            align: 'right',
            render: (_, r) => formatNumber(r.simAllocated),
        },
        {
            key: 'simActivated',
            title: 'SIM đấu mới',
            align: 'right',
            render: (_, r) => formatNumber(r.simActivated),
        },
        {
            key: 'renewalCount',
            title: 'Lượt gia hạn',
            align: 'right',
            render: (_, r) => formatNumber(r.renewalCount),
        },
        {
            key: 'activationRate',
            title: 'Tỷ lệ đấu',
            align: 'right',
            render: (_, r) => formatPercent(r.activationRate),
        },
        {
            key: 'revenue',
            title: 'Doanh thu',
            align: 'right',
            render: (_, r) => formatCurrency(r.revenue),
        },
        {
            key: 'overdueCount',
            title: 'Tồn quá hạn ⚠️',
            align: 'right',
            render: (_, r) => (
                <span style={{ color: r.overdueCount > 0 ? '#ec4969' : undefined, fontWeight: r.overdueCount > 0 ? 700 : undefined }}>
                    {formatNumber(r.overdueCount)}
                </span>
            ),
        },
    ]

    return (
        <ConfigProvider locale={vi_VN}>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='range' label='Khoảng ngày' style={{ minWidth: 300 }}>
                    <DateRangePresets />
                </Form.Item>
                <Form.Item name='cpCodes' label='Công ty' style={{ minWidth: 240 }}>
                    <CompanySelect multiple onlyWithOutlets placeholder='Chọn công ty' />
                </Form.Item>
                <Form.Item name='outletIds' label='Đại lý' style={{ minWidth: 240 }}>
                    <OutletSelect multiple placeholder='Chọn đại lý' />
                </Form.Item>
                <Form.Item name='groupBy' label='Nhóm theo' initialValue='province'>
                    <Radio.Group>
                        <Radio.Button value='province'>Tỉnh/TP</Radio.Button>
                        <Radio.Button value='district'>Quận/Huyện</Radio.Button>
                    </Radio.Group>
                </Form.Item>
            </FormSearch>

            <CustomTable
                namePage='khu vực'
                rowKey={(r: IRegionRow) => `${r.province ?? ''}_${r.district ?? ''}`}
                columns={columns}
                dataSource={data}
                total={data.length}
                pageSize={data.length || 10}
                setPageSize={() => { }}
                currentPage={1}
                setCurrentPage={() => { }}
                isHiddenPagination
            />
        </ConfigProvider>
    )
}
